'use client';

import { useState, useEffect } from 'react';

import { cardsAPI } from '../../../../../api/modules/cards.module';
import { useListing } from '../../../../../Contexts/ListingProvider';
import { useCardLookup } from '../../../../../hooks/useCardLookup';
import { useListingSuggestedPrice } from '../../../../../hooks/useListingSuggestedPrice';
import CardDetailsStepView from './CardDetailsStep.view';

/**
 * Step 1, card details. The container.
 *
 * Owns the debounced catalogue search, the card lookup and the suggested-price
 * warm-up. Every request, query key and effect below is the one that was here
 * before; only the rendering moved to `CardDetailsStep.view.jsx`.
 */

export const CONDITION_OPTIONS = [
  {
    value: 'Near Mint',
    label: 'Near Mint (NM)',
    description: 'Excellent condition, minor handling',
  },
  {
    value: 'Lightly Played',
    label: 'Lightly Played (LP)',
    description: 'Minor wear, still great condition',
  },
  {
    value: 'Moderately Played',
    label: 'Moderately Played (MP)',
    description: 'Noticeable wear but playable',
  },
  {
    value: 'Heavily Played',
    label: 'Heavily Played (HP)',
    description: 'Significant wear, still intact',
  },
  {
    value: 'Damaged',
    label: 'Damaged (DMG)',
    description: 'Major damage, structural issues',
  },
];

/**
 * Sealed product is not graded, so it gets its own one-item vocabulary rather
 * than a grade off the ladder above.
 *
 * This is not cosmetic. The catalogue holds exactly one price row for a sealed
 * box and it sits at condition "Sealed" — a play grade has never existed for
 * one. Tracked listings now price from the EXACT (finish, condition) row
 * (issue #108), so a sealed box offered "Near Mint" matches no row at all and
 * can never be listed as tracked. "Sealed" matches the row it actually has.
 */
export const SEALED_CONDITION_OPTIONS = [
  {
    value: 'Sealed',
    label: 'Sealed',
    description: 'Factory sealed, never opened',
  },
];

const CardDetailsStep = ({ formData, updateFormData, errors, titleRef }) => {
  const { Setter } = useListing();
  const selectedCardId = formData.card_id?.trim() || null;
  const lookup = useCardLookup(selectedCardId);
  const finishOptions = lookup.data?.available_finishes ?? [];
  const conditionOptions = lookup.data?.is_sealed ? SEALED_CONDITION_OPTIONS : CONDITION_OPTIONS;

  // Warm the suggested-price cache the moment a card is selected: the
  // backend's JustTCG refresh (1-2s worst case) runs while the seller is
  // still on the details/images steps. PricingQuantityStep reads the same
  // query key from cache.
  useListingSuggestedPrice(selectedCardId, formData.finish, formData.condition);

  const [cardOptions, setCardOptions] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectName, setSelectedName] = useState(null);

  // Fetch cards when search term changes
  useEffect(() => {
    const fetchCards = async () => {
      if (!searchTerm.trim()) {
        setCardOptions([]);
        return;
      }

      setLoading(true); // Start loading

      try {
        const data = await cardsAPI.getAvailableCards(searchTerm);
        setCardOptions(data?.data);
      } catch (error) {
        console.error('Failed to fetch cards:', error);
      } finally {
        setLoading(false); // End loading
      }
    };

    const debounceTimer = setTimeout(fetchCards, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  useEffect(() => {
    if (lookup.data) {
      Setter(lookup.data);
      const finishes = lookup.data.available_finishes ?? [];
      if (!finishes.includes(formData.finish)) {
        updateFormData('finish', finishes[0] || 'Normal');
      }
      // Same correction for condition: the seller may arrive carrying a play
      // grade from a previously selected single card, and a sealed box has no
      // such grade (nor the reverse). Leave a valid choice alone.
      const conditions = (lookup.data.is_sealed ? SEALED_CONDITION_OPTIONS : CONDITION_OPTIONS).map(
        (opt) => opt.value
      );
      if (!conditions.includes(formData.condition)) {
        updateFormData('condition', conditions[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.data, formData.finish, formData.condition]);

  const handleCardSelect = (cardId, title) => {
    setSelectedName(title);
    updateFormData('card_id', cardId);
    updateFormData('finish', 'Normal');
    setIsDropdownOpen(false);
  };

  return (
    <CardDetailsStepView
      titleRef={titleRef}
      formData={formData}
      updateFormData={updateFormData}
      errors={errors}
      // The preview's source. `lookup.data` is the same object the rail and
      // the review step already read through `ListingProvider`, and it is
      // keyed by card id, so a late response for a card the seller has moved
      // off can never paint the wrong art under the wrong name.
      card={lookup.data || null}
      cardLoading={Boolean(selectedCardId) && lookup.isFetching && !lookup.data}
      conditionOptions={conditionOptions}
      finishOptions={finishOptions}
      finishDisabled={finishOptions.length <= 1 || lookup.isFetching}
      searchTerm={searchTerm}
      onSearchTermChange={(value) => {
        setSearchTerm(value);
        setSelectedName(null);
        setIsDropdownOpen(true);
      }}
      onOpenResults={() => setIsDropdownOpen(true)}
      resultsOpen={isDropdownOpen}
      results={cardOptions || []}
      loading={loading}
      selectedName={selectName}
      onSelectCard={handleCardSelect}
    />
  );
};

export default CardDetailsStep;
